import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const emptyMeal = () => ({ name: '', protein: '', calories: '' });

const emptyDay = (day) => ({
    day,
    breakfast: emptyMeal(),
    lunch: emptyMeal(),
    dinner: emptyMeal(),
    nonveg_item: emptyMeal(),
    nonveg_item_2: emptyMeal(),
    instructions: '',
    warnings: '',
});

// Defined outside WeeklyMenuTab so React never remounts it on re-render
const MealInput = ({ label, value, onChangeName, onChangeProtein, onChangeCalories }) => (
    <div>
        <label className="block text-sm font-medium text-white mb-2">{label}</label>
        <input
            type="text"
            value={value?.name || ''}
            onChange={onChangeName}
            placeholder="Meal name"
            className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-2"
        />
        <div className="grid grid-cols-2 gap-2">
            <input
                type="number"
                value={value?.protein || ''}
                onChange={onChangeProtein}
                placeholder="Protein (g)"
                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <input
                type="number"
                value={value?.calories || ''}
                onChange={onChangeCalories}
                placeholder="Calories"
                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
        </div>
    </div>
);

const WeeklyMenuTab = ({ setMessage, setError }) => {
    const [menuDays, setMenuDays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const initializeMenu = () => {
        setMenuDays(WEEKDAYS.map(emptyDay));
    };

    useEffect(() => {
        loadWeeklyMenu();
    }, []);

    const loadWeeklyMenu = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await http.get('/admin/weekly-menu', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && Array.isArray(response.data.menu_days)) {
                const fetched = response.data.menu_days;
                const filled = WEEKDAYS.map((day) => {
                    const found = fetched.find((d) => d.day === day);
                    return found || emptyDay(day);
                });
                setMenuDays(filled);
            }
        } catch (error) {
            console.error('Failed to load weekly menu:', error);
            setError(error.response?.data?.detail || 'Failed to load menu');
        } finally {
            setLoading(false);
        }
    };

    const handleMealChange = (dayIndex, meal, field, value) => {
        const updated = [...menuDays];
        updated[dayIndex] = {
            ...updated[dayIndex],
            [meal]: { ...updated[dayIndex][meal], [field]: value },
        };
        setMenuDays(updated);
    };

    const handleFieldChange = (dayIndex, field, value) => {
        const updated = [...menuDays];
        updated[dayIndex] = { ...updated[dayIndex], [field]: value };
        setMenuDays(updated);
    };

    const saveWeeklyMenu = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            await http.post(
                '/admin/weekly-menu',
                { menu_days: menuDays },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage('Weekly menu saved successfully');
        } catch (error) {
            console.error('Failed to save weekly menu:', error);
            setError(error.response?.data?.detail || 'Failed to save menu');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center py-8 text-gray-900">Loading menu...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl p-6 shadow-lg border-2 border-orange-500">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                            <svg className="h-7 w-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Weekly Menu Management
                        </h3>
                        <p className="text-gray-300 text-sm mt-2">Set menu for kitchen team (Monday – Friday)</p>
                    </div>
                    <button
                        onClick={saveWeeklyMenu}
                        disabled={saving}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        {saving ? 'Saving...' : 'Save Menu'}
                    </button>
                </div>
            </div>

            {menuDays.length === 0 && (
                <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-dashed border-orange-500 p-12 text-center">
                    <svg className="h-16 w-16 text-orange-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h4 className="text-xl font-bold text-white mb-2">No Weekly Menu Created Yet</h4>
                    <p className="text-gray-400 mb-6">Create the weekly menu template to start managing the kitchen schedule.</p>
                    <button
                        onClick={initializeMenu}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 rounded-lg font-semibold shadow-lg transition-all inline-flex items-center gap-2"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Weekly Menu
                    </button>
                </div>
            )}

            <div className="space-y-4">
                {menuDays.map((day, index) => (
                    <div key={day.day} className="bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-orange-500 overflow-hidden shadow-2xl">
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3">
                            <h4 className="font-bold text-xl text-white">{day.day}</h4>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 gap-4">
                                <MealInput
                                    label="Breakfast"
                                    value={day.breakfast}
                                    onChangeName={(e) => handleMealChange(index, 'breakfast', 'name', e.target.value)}
                                    onChangeProtein={(e) => handleMealChange(index, 'breakfast', 'protein', e.target.value)}
                                    onChangeCalories={(e) => handleMealChange(index, 'breakfast', 'calories', e.target.value)}
                                />
                                <MealInput
                                    label="Lunch"
                                    value={day.lunch}
                                    onChangeName={(e) => handleMealChange(index, 'lunch', 'name', e.target.value)}
                                    onChangeProtein={(e) => handleMealChange(index, 'lunch', 'protein', e.target.value)}
                                    onChangeCalories={(e) => handleMealChange(index, 'lunch', 'calories', e.target.value)}
                                />
                                <MealInput
                                    label="Dinner"
                                    value={day.dinner}
                                    onChangeName={(e) => handleMealChange(index, 'dinner', 'name', e.target.value)}
                                    onChangeProtein={(e) => handleMealChange(index, 'dinner', 'protein', e.target.value)}
                                    onChangeCalories={(e) => handleMealChange(index, 'dinner', 'calories', e.target.value)}
                                />
                                <MealInput
                                    label="Non-Veg Item"
                                    value={day.nonveg_item}
                                    onChangeName={(e) => handleMealChange(index, 'nonveg_item', 'name', e.target.value)}
                                    onChangeProtein={(e) => handleMealChange(index, 'nonveg_item', 'protein', e.target.value)}
                                    onChangeCalories={(e) => handleMealChange(index, 'nonveg_item', 'calories', e.target.value)}
                                />
                                <MealInput
                                    label="Non-Veg Item 2"
                                    value={day.nonveg_item_2}
                                    onChangeName={(e) => handleMealChange(index, 'nonveg_item_2', 'name', e.target.value)}
                                    onChangeProtein={(e) => handleMealChange(index, 'nonveg_item_2', 'protein', e.target.value)}
                                    onChangeCalories={(e) => handleMealChange(index, 'nonveg_item_2', 'calories', e.target.value)}
                                />
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-white mb-2">Instructions for Team</label>
                                    <textarea
                                        value={day.instructions || ''}
                                        onChange={(e) => handleFieldChange(index, 'instructions', e.target.value)}
                                        placeholder="Special cooking instructions..."
                                        rows={2}
                                        className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-orange-500 mb-2 flex items-center gap-2">
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        Warnings for Team
                                    </label>
                                    <textarea
                                        value={day.warnings || ''}
                                        onChange={(e) => handleFieldChange(index, 'warnings', e.target.value)}
                                        placeholder="Important warnings (allergies, safety notes, etc.)"
                                        rows={2}
                                        className="w-full border-2 border-orange-500 rounded-lg px-3 py-2 bg-orange-900/20 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WeeklyMenuTab;
